import { useRef, useState } from 'react';
import { Link, useLocation } from 'react-router';
import { categoryLabel, type ProductSummary } from '@va/shared';
import { mediaUrl } from '../platform';
import { PlatformIcons, Price } from './ui';

export function ProductCard({ product }: { product: ProductSummary }) {
  const loc = useLocation();
  // The store list to return to: this page, or (on a product page, e.g. its bundles) the list
  // that product was opened from.
  const incoming = (loc.state as { from?: string } | null)?.from;
  const from = loc.pathname.startsWith('/products/') && incoming ? incoming : loc.pathname + loc.search;
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const owned = product.ownership?.owned;
  const trial = owned && product.ownership!.sources.every((s) => s === 'trial');

  const start = () => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = 0;
    v.play().then(() => setPlaying(true)).catch(() => {});
  };
  const stop = () => {
    videoRef.current?.pause();
    setPlaying(false);
  };

  return (
    <Link to={`/products/${product.slug}`} state={{ from }} className="product-card" onMouseEnter={start} onMouseLeave={stop} onFocus={start} onBlur={stop}>
      <div className={`product-media${playing ? ' playing' : ''}`}>
        {product.thumbnailUrl ? (
          <img src={mediaUrl(product.thumbnailUrl)} alt="" loading="lazy" />
        ) : (
          <div className="placeholder">{product.name.slice(0, 2).toUpperCase()}</div>
        )}
        {product.previewVideoUrl && <video ref={videoRef} src={mediaUrl(product.previewVideoUrl)} muted loop playsInline preload="none" />}
        <div className="media-badges">
          <span className="pill indigo">{categoryLabel(product.categories)}</span>
          <span className="media-badges-end">
            {product.isNew && !owned && <span className="pill solid">New</span>}
            {owned && !trial && <span className="pill success">Owned</span>}
            {trial && <span className="pill warning">Trial</span>}
          </span>
        </div>
      </div>
      <div className="product-body">
        <h3 className="product-name">{product.name}</h3>
        <p className="product-tagline">{product.tagline}</p>
        <div className="product-foot">
          <PlatformIcons platforms={product.platforms} />
          {owned && !trial ? <span className="pill success">In My Library</span> : <Price {...product} />}
        </div>
      </div>
    </Link>
  );
}
